import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";

import PrimerSection from "./sections/PrimerSection";
import SKPSection from "./sections/SKPSection";
import HACCPSection from "./sections/HACCPSection";

import PeriodFilter from "./components/filters/PeriodFilter";
import { formatPeriodLabel } from "./utils/formatPeriodLabel";
import { validatePeriod } from "./utils/validatePeriod";

import DashboardService from "../../services/pbumku/DashboardService";

const currentYear = new Date().getFullYear();
const getStartOfYear = () => `${currentYear}-01-01`;
const getEndOfYear = () => `${currentYear}-12-31`;

export default function DashboardGraphPBUMKU() {
  const DashService = useMemo(() => new DashboardService(), []);

  const [rekapPrimer, setRekapPrimer] = useState(null);
  const [rekapSKP, setRekapSKP] = useState([]);
  const [rekapHACCPGrade, setRekapHACCPGrade] = useState([]);

  const [limitPrimer, setLimitPrimer] = useState(8);
  const [limitSKP, setLimitSKP] = useState(5);
  const [limitHACCP, setLimitHACCP] = useState(5);

  const [totalTerbitSKP, setTotalTerbitSKP] = useState(0);

  const formatDate = (date, isEndOfDay = false) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const time = isEndOfDay ? "23:59:59" : "00:00:00";
    return `${yyyy}-${mm}-${dd} ${time}`;
  };

  const [tempStartDate, setTempStartDate] = useState(getStartOfYear());
  const [tempEndDate, setTempEndDate] = useState(getEndOfYear());

  const [appliedStartDate, setAppliedStartDate] = useState(getStartOfYear());
  const [appliedEndDate, setAppliedEndDate] = useState(getEndOfYear());

  const tahunBerjalan = currentYear;

  const handleClickButton = () => {
    const validation = validatePeriod(tempStartDate, tempEndDate, {
      maxYearRange: 1,
      allowCrossYear: true,
    });

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setAppliedStartDate(tempStartDate);
    setAppliedEndDate(tempEndDate);
  };

  /* ================= FETCH DATA ================= */

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const start = appliedStartDate.slice(0, 10);
        const end = appliedEndDate.slice(0, 10);

        const [primer, skp, totalSKP, haccp] = await Promise.all([
          DashService.getRekapPermohonan(start, end, limitPrimer),
          DashService.getSKPPerPropinsi(start, end, limitSKP),
          DashService.getJumlahTotalSKPTerbit(start, end),
          DashService.getHACCPPerPropinsiPerGrade(start, end, limitHACCP),
        ]);

        setRekapPrimer(primer.data);
        setRekapSKP(skp.data);
        setTotalTerbitSKP(totalSKP.data.sertifikat || 0);
        setRekapHACCPGrade(haccp.data);

      } catch (err) {
        console.error("Fetch dashboard error:", err);
      }
    };

    fetchAll();
  }, [
    appliedStartDate,
    appliedEndDate,
    limitPrimer,
    limitSKP,
    limitHACCP,
    DashService
  ]);


  return (
    <>
      {/* ===== HEADER + FILTER ===== */}
      <Card className="mb-4 bg-transparent border-0 shadow-none">
        <Card.Body className="px-1 py-0">
          <Row className="align-items-end">
            <Col md={6}>
              <h3 className="text-white fw-bold mb-2">
                DASHBOARD SERTIFIKASI MUTU BPPMHKP
              </h3>
              <div className="text-white">
                {formatPeriodLabel(appliedStartDate, appliedEndDate)}
              </div>
            </Col>

            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
              <PeriodFilter
                startDate={tempStartDate}
                endDate={tempEndDate}
                onChangeStart={setTempStartDate}
                onChangeEnd={setTempEndDate}
                onSubmit={handleClickButton}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== CONTENT ===== */}
      <Row className="gx-3 gy-3">
        <Col md={6}>
          <PrimerSection
            data={rekapPrimer}
            limit={limitPrimer}
            onChangeLimit={setLimitPrimer}
            thn={tahunBerjalan}
          />
        </Col>

        <Col md={6}>
          <SKPSection
            data={rekapSKP}
            totalSKP={totalTerbitSKP}
            limit={limitSKP}
            onChangeLimit={setLimitSKP}
            thn={tahunBerjalan}
          />

          <HACCPSection
            data={rekapHACCPGrade}
            totalTerbitSKP={totalTerbitSKP}
            limit={limitHACCP}
            onChangeLimit={setLimitHACCP}
            thn={tahunBerjalan}
          />
        </Col>
      </Row>
    </>
  );
}
